import { RiHome2Line } from "@itandi/itandi-bb-ui";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputDatev2 } from "@itandi/itandi-bb-ui/InputDatev2";
import { InputRadio } from "@itandi/itandi-bb-ui/InputRadio";
import { PresentationChip } from "@itandi/itandi-bb-ui/PresentationChip";
import React from "react";
import { useFormContext } from "react-hook-form";
import { RoomBlockPropertySelectV2 } from "~/components/common/deals/new/block/RoomBlockPropertySelectV2";
import type { DealParam } from "~/models/DealForm";
import type { Leasing } from "~/models/Leasing";
import type { PropertyRoomOption } from "~/models/PropertyRoomOption";
import { formatDate, toDate } from "~/utils/DateUtil";
import { isFile } from "~/utils/FileUtil";
import Styles from "./Block.module.scss";

type Props = {
	leasing: Leasing | null;
	selectedRoom?: PropertyRoomOption | null;
	setSelectedRoom: (property: PropertyRoomOption | null) => void;
};

export const RoomBlock: React.FC<Props> = ({
	leasing = null,
	selectedRoom = null,
	setSelectedRoom
}: Props) => {
	const { register, formState, setValue, watch } = useFormContext<DealParam>();
	const { errors } = formState;

	const onChangeFloorMap = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>): void => {
			const file = e.target.files?.[0];
			if (file) {
				setValue("floorMap", file);
			}
		},
		[setValue]
	);

	const floorMap = watch("floorMap");
	const preferredDate = watch("preferredDate");
	const onClickFloorMapLink = React.useCallback(
		(e: React.MouseEvent): void => {
			e.preventDefault();
			if (isFile(floorMap)) {
				const url = URL.createObjectURL(floorMap);
				window.open(url);
			}
		},
		[floorMap]
	);
	return (
		<div className={Styles.Block}>
			<div className={Styles.BlockCaptionWrapper}>
				<div className={Styles.BlockCaption}>
					<span className={Styles.BlockIconWrapper}>
						<RiHome2Line className={Styles.BlockIcon} />
					</span>
					<span className={Styles.BlockCaptionLabel}>物件情報</span>
				</div>
			</div>
			<div className={Styles.BlockForms}>
				<RoomBlockPropertySelectV2
					leasing={leasing}
					selectedRoom={selectedRoom}
					setSelectedRoom={setSelectedRoom}
				/>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>エレベーターの有無</span>
						<PresentationChip variant="red">必須</PresentationChip>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputRadioGroup}>
							<InputRadio
								checked={watch("enableElevator") === "true"}
								isInvalid={!!errors.enableElevator}
								label="あり"
								value="true"
								{...register("enableElevator")}
							/>
							<InputRadio
								checked={watch("enableElevator") === "false"}
								isInvalid={!!errors.enableElevator}
								label="なし"
								value="false"
								{...register("enableElevator")}
							/>
						</div>
						{errors.enableElevator ? (
							<div className={Styles.ErrorMessage}>
								{errors.enableElevator.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>オートロックの有無</span>
						<PresentationChip variant="red">必須</PresentationChip>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputRadioGroup}>
							<InputRadio
								checked={watch("enableAutoLock") === "true"}
								isInvalid={!!errors.enableAutoLock}
								label="あり"
								value="true"
								{...register("enableAutoLock")}
							/>
							<InputRadio
								checked={watch("enableAutoLock") === "false"}
								isInvalid={!!errors.enableAutoLock}
								label="なし"
								value="false"
								{...register("enableAutoLock")}
							/>
						</div>
						{errors.enableAutoLock ? (
							<div className={Styles.ErrorMessage}>
								{errors.enableAutoLock.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>鍵の置き場所</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputWrapper}>
							<Input
								isInvalid={!!errors.placeKeyStoring}
								placeholder="例）エントランス横の雨樋（番号：1234）"
								{...register("placeKeyStoring")}
							/>
						</div>
						{errors.placeKeyStoring ? (
							<div className={Styles.ErrorMessage}>
								{errors.placeKeyStoring.message}
							</div>
						) : null}
						<div className={Styles.Description}>
							下記について教えてください。
							<br />
							空室時：鍵を設置した場所
							<br />
							立会依頼時：鍵設置を希望する場所（希望がない場合は「希望なし」とご記載ください）
						</div>
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>無償利用が可能な駐車場</span>
					</div>
					<div className={Styles.FormContent}>
						<div className={Styles.InputWrapper}>
							<Input
								isInvalid={!!errors.freeParking}
								placeholder="例）エントランス前"
								{...register("freeParking")}
							/>
						</div>
						{errors.freeParking ? (
							<div className={Styles.ErrorMessage}>
								{errors.freeParking.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>間取り図</span>
					</div>
					<div className={Styles.FormContent}>
						{isFile(floorMap) && (
							<div>
								<a
									className={Styles.FileLink}
									href="#"
									onClick={onClickFloorMapLink}
									rel="noreferrer"
									target="_blank"
								>
									{floorMap.name}
								</a>
							</div>
						)}
						<div className={Styles.InputWrapper}>
							<input
								accept="image/*,application/pdf"
								onChange={onChangeFloorMap}
								type="file"
							/>
						</div>
						{errors.floorMap ? (
							<div className={Styles.ErrorMessage}>
								{errors.floorMap.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormRow}>
					<div className={Styles.FormLabel}>
						<span className={Styles.FormLabelText}>入居予定日</span>
					</div>
					<div className={Styles.FormContent}>
						<div>
							<InputDatev2
								isInvalid={!!errors.preferredDate}
								maxWidth="13rem"
								onChange={(date): void => {
									setValue("preferredDate", formatDate(date));
								}}
								value={toDate(preferredDate)}
							/>
						</div>
						<div className={Styles.Description}>
							次回の入居予定がある場合は予定日を入力してください。
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
